/* DSR FORM — Restructured (Sep 11 2026 client spec).
 *
 * Top-level tabs:
 *   1. Sales Reports   — Store Details / Fresh / EOSS / Return Orders
 *   2. Gift Box Order  — Q25-Q33
 *   3. Delivery & Payment — Q18-Q24 + Q34-Q39
 *   4. Purifier Details   — Q40-Q46
 *   5. Review & Submit
 *
 * Fresh & EOSS each carry Qty / MRP / Net + 7 category-wise quantities.
 * EOSS tab & Return Orders EOSS block auto-disable when the location is
 * outside its configured EOSS period.
 *
 * Retailer edit rule: within 45 days of submission, retailers can edit their
 * own rows via the responses table (mandatory Remarks). Admins can always
 * edit.
 */
import { useEffect, useMemo, useState, Fragment } from "react";
import { http, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
    Check, ChevronRight, Store, Shirt, Percent, User as UserIcon, Send,
    Sparkles, Lock, Gift, Truck, Droplet, RotateCcw, Pencil, X, AlertCircle,
} from "lucide-react";

const ADMIN_ROLES = new Set(["admin", "spykar_admin", "system_admin", "state_manager"]);
const CAT_KEYS = [
    ["basic_denim", "BASIC DENIM"],
    ["actif_denim", "ACTIF DENIM"],
    ["trousers", "TROUSERS"],
    ["shirts", "SHIRTS"],
    ["tshirt", "T-SHIRT"],
    ["sweatshirt", "SWEATSHIRT"],
    ["jackets", "JACKETS"],
];

const emptyForm = () => ({
    date: "", store_name: "", location: "", staff_name: "",
    // Fresh
    fresh_qty: "", fresh_mrp_val: "", fresh_net_val: "",
    fresh_cat_basic_denim: "", fresh_cat_actif_denim: "", fresh_cat_trousers: "",
    fresh_cat_shirts: "", fresh_cat_tshirt: "", fresh_cat_sweatshirt: "", fresh_cat_jackets: "",
    // EOSS
    eoss_qty: "", eoss_mrp_val: "", eoss_net_val: "",
    eoss_cat_basic_denim: "", eoss_cat_actif_denim: "", eoss_cat_trousers: "",
    eoss_cat_shirts: "", eoss_cat_tshirt: "", eoss_cat_sweatshirt: "", eoss_cat_jackets: "",
    // Returns
    fresh_returns_qty: "", fresh_returns_mrp_val: "", fresh_returns_net_val: "",
    eoss_returns_qty: "", eoss_returns_mrp_val: "", eoss_returns_net_val: "",
    returns_remarks: "",
    // Q18-Q46
    q18_full_name: "", q19_phone_number: "", q20_email: "", q21_shipping_address: "",
    q22_city: "", q23_state: "", q24_zip_code: "",
    q25_gift_box_package: "", q26_gift_box_set: "", q27_quantity: "", q28_gift_for: "",
    q29_personalized_message: "", q30_personalized_message_to: "", q31_personalized_message_text: "",
    q32_ship_to_receiver: "", q33_gift_receiver_address: "",
    q34_payment_method: "", q35_billing_address: "", q36_hear_about: "",
    q37_further_comments: "", q38_email_address: "", q39_address: "",
    q40_appointment_date: "", q41_appointment_time: "",
    q42_water_purifier_model: "", q43_water_purifier_years: "", q44_water_purifier_warranty: "",
    q45_water_purifier_problem: "", q46_special_instructions: "",
});

const n = (v) => Number(v || 0);
const NUM_KEYS = [
    "fresh_qty", "fresh_mrp_val", "fresh_net_val",
    "fresh_cat_basic_denim", "fresh_cat_actif_denim", "fresh_cat_trousers",
    "fresh_cat_shirts", "fresh_cat_tshirt", "fresh_cat_sweatshirt", "fresh_cat_jackets",
    "eoss_qty", "eoss_mrp_val", "eoss_net_val",
    "eoss_cat_basic_denim", "eoss_cat_actif_denim", "eoss_cat_trousers",
    "eoss_cat_shirts", "eoss_cat_tshirt", "eoss_cat_sweatshirt", "eoss_cat_jackets",
    "fresh_returns_qty", "fresh_returns_mrp_val", "fresh_returns_net_val",
    "eoss_returns_qty", "eoss_returns_mrp_val", "eoss_returns_net_val",
];

const TABS = [
    { id: "sales", title: "Sales Reports", icon: Store },
    { id: "gift", title: "Gift Box Order", icon: Gift },
    { id: "delivery", title: "Delivery & Payment", icon: Truck },
    { id: "purifier", title: "Purifier Details", icon: Droplet },
    { id: "review", title: "Review & Submit", icon: Send },
];

const SUB_TABS = [
    { id: "store", title: "Store Details", icon: Store },
    { id: "fresh", title: "Fresh", icon: Shirt },
    { id: "eoss", title: "EOSS", icon: Percent },
    { id: "returns", title: "Return Orders", icon: RotateCcw },
];

export default function GujratDsrForm() {
    const { user } = useAuth();
    const [form, setForm] = useState(emptyForm);
    const [saving, setSaving] = useState(false);
    const [rows, setRows] = useState([]);
    const [hierarchy, setHierarchy] = useState([]);
    const [eossMap, setEossMap] = useState({});
    const [prefilled, setPrefilled] = useState(false);
    const [tab, setTab] = useState("sales");
    const [subTab, setSubTab] = useState("store");
    const [editRow, setEditRow] = useState(null);

    useEffect(() => {
        http.get("/reports/gujrat-dsr", { params: { limit: 50 } }).then((r) => setRows(r.data)).catch(() => {});
        http.get("/admin/hierarchy").then((r) => setHierarchy(r.data || [])).catch(() => {});
        http.get("/eoss-config/active").then((r) => setEossMap(r.data || {})).catch(() => {});
    }, []);

    const isAdmin = user && ADMIN_ROLES.has(user.role);
    const scopedOutlets = useMemo(() => {
        const flat = new Set(user?.scope?.outlets || []);
        const dims = ["rsm", "asm", "distributor", "retailer"];
        const dimNames = {};
        for (const d of dims) dimNames[d] = new Set(user?.scope?.[d] || []);
        for (const row of hierarchy) {
            for (const d of dims) if (dimNames[d].has(row[d])) flat.add(row.outlet);
        }
        return [...flat];
    }, [user, hierarchy]);
    const outletToCity = useMemo(() => {
        const m = {};
        for (const row of hierarchy) if (row.outlet) m[row.outlet] = row.city;
        return m;
    }, [hierarchy]);
    const storeOptions = isAdmin
        ? (hierarchy.length ? [...new Set(hierarchy.map((r) => r.outlet))].sort() : [])
        : (scopedOutlets.length ? scopedOutlets : []);
    const locationOptions = isAdmin
        ? (hierarchy.length ? [...new Set(hierarchy.map((r) => r.city))].sort() : [])
        : [...new Set(scopedOutlets.map((o) => outletToCity[o]).filter(Boolean))];
    const lockStore = !isAdmin && scopedOutlets.length === 1;
    const lockLocation = !isAdmin && scopedOutlets.length === 1;
    const lockStaff = !isAdmin && user?.role === "retailer";
    const eossActive = form.location ? (eossMap[form.location] !== false) : true;

    useEffect(() => {
        if (prefilled || !user) return;
        if (isAdmin) { setPrefilled(true); return; }
        if (hierarchy.length === 0) return;
        const outlet = scopedOutlets.length === 1 ? scopedOutlets[0] : "";
        const city = outlet ? (outletToCity[outlet] || "") : "";
        setForm((f) => ({
            ...f,
            store_name: outlet || f.store_name,
            location: city || f.location,
            staff_name: user.name || f.staff_name,
        }));
        setPrefilled(true);
    }, [user, scopedOutlets, outletToCity, hierarchy, prefilled, isAdmin]);

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const validateAll = () => {
        for (const f of ["date", "store_name", "location", "staff_name"]) {
            if (!String(form[f] || "").trim()) {
                setTab("sales"); setSubTab("store");
                return `${f.replace("_", " ").toUpperCase()} is required`;
            }
        }
        for (const f of ["fresh_qty", "fresh_mrp_val", "fresh_net_val"]) {
            if (String(form[f] ?? "").trim() === "") {
                setTab("sales"); setSubTab("fresh");
                return `${f.replace(/_/g, " ").toUpperCase()} is required`;
            }
        }
        if (eossActive) {
            for (const f of ["eoss_qty", "eoss_mrp_val", "eoss_net_val"]) {
                if (String(form[f] ?? "").trim() === "") {
                    setTab("sales"); setSubTab("eoss");
                    return `${f.replace(/_/g, " ").toUpperCase()} is required`;
                }
            }
        }
        return null;
    };

    const submit = async () => {
        const err = validateAll();
        if (err) { toast.error(err); return; }
        setSaving(true);
        try {
            const payload = { ...form };
            NUM_KEYS.forEach((k) => (payload[k] = n(form[k])));
            // Legacy combined categories = fresh_cat + eoss_cat
            CAT_KEYS.forEach(([k]) => {
                payload[k] = n(form[`fresh_cat_${k}`]) + n(form[`eoss_cat_${k}`]);
            });
            Object.keys(payload).forEach((k) => {
                if (k.startsWith("q") && payload[k] === "") payload[k] = null;
            });
            const r = await http.post("/reports/gujrat-dsr", payload);
            toast.success("Response submitted");
            setForm(emptyForm());
            setPrefilled(false);
            setTab("sales"); setSubTab("store");
            setRows((prev) => [r.data, ...prev]);
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        } finally { setSaving(false); }
    };

    return (
        <div className="mx-auto max-w-5xl space-y-5 pb-16" data-testid="gujrat-dsr-form">
            <div className="flex items-end justify-between">
                <div>
                    <h1 className="text-[22px] font-semibold tracking-tight" data-testid="gdsr-title">DSR FORM</h1>
                    <div className="mt-0.5 text-[12px] text-muted-foreground">
                        Daily Sales Report · 4 sections + review
                    </div>
                </div>
                <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
                    {user?.name} · <span className="text-teal-600 font-semibold">{user?.role}</span>
                </div>
            </div>

            {/* Top-level tab bar */}
            <TabBar tabs={TABS} current={tab} onJump={setTab} />

            <div className="rounded-md border border-border bg-card p-6" data-testid={`gdsr-tab-${tab}`}>
                {tab === "sales" && (
                    <SalesTab
                        form={form} set={set}
                        subTab={subTab} setSubTab={setSubTab}
                        storeOptions={storeOptions} locationOptions={locationOptions}
                        outletToCity={outletToCity}
                        lockStore={lockStore} lockLocation={lockLocation} lockStaff={lockStaff}
                        user={user} eossActive={eossActive}
                    />
                )}
                {tab === "gift" && <GiftTab form={form} set={set} />}
                {tab === "delivery" && <DeliveryTab form={form} set={set} />}
                {tab === "purifier" && <PurifierTab form={form} set={set} />}
                {tab === "review" && (
                    <ReviewTab form={form} onEdit={setTab} onSubmit={submit} saving={saving} eossActive={eossActive} />
                )}
            </div>

            <ResponsesTable rows={rows} user={user} onEdit={setEditRow} />
            {editRow && (
                <EditModal
                    row={editRow}
                    onClose={() => setEditRow(null)}
                    onSaved={(updated) => {
                        setRows((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
                        setEditRow(null);
                    }}
                />
            )}
        </div>
    );
}

// ---------------------------------------------------------------------------
// Tab bars
// ---------------------------------------------------------------------------
function TabBar({ tabs, current, onJump, testidPrefix = "gdsr-tab" }) {
    return (
        <div className="flex flex-wrap items-stretch gap-1" data-testid={`${testidPrefix}bar`}>
            {tabs.map((t) => {
                const isCurrent = t.id === current;
                return (
                    <button
                        key={t.id}
                        onClick={() => onJump(t.id)}
                        data-testid={`${testidPrefix}-${t.id}`}
                        className={[
                            "flex flex-1 min-w-[140px] items-center gap-2 rounded-md border px-3 py-2 text-left text-[12px] transition",
                            isCurrent
                                ? "border-teal-600 bg-teal-50 shadow-sm dark:bg-teal-950/30"
                                : "border-border bg-card hover:bg-muted/30",
                        ].join(" ")}
                    >
                        <t.icon className="h-4 w-4 text-teal-600" />
                        <span className="truncate font-semibold">{t.title}</span>
                    </button>
                );
            })}
        </div>
    );
}

// ---------------------------------------------------------------------------
// SALES tab (with sub-tabs: Store / Fresh / EOSS / Returns)
// ---------------------------------------------------------------------------
function SalesTab({ form, set, subTab, setSubTab, storeOptions, locationOptions, outletToCity,
                   lockStore, lockLocation, lockStaff, user, eossActive }) {
    return (
        <div className="space-y-4">
            <TabBar tabs={SUB_TABS} current={subTab} onJump={setSubTab} testidPrefix="gdsr-subtab" />
            {subTab === "store" && (
                <StoreStep
                    form={form} set={set}
                    storeOptions={storeOptions} locationOptions={locationOptions}
                    outletToCity={outletToCity}
                    lockStore={lockStore} lockLocation={lockLocation} lockStaff={lockStaff}
                    user={user}
                />
            )}
            {subTab === "fresh" && <FreshStep form={form} set={set} />}
            {subTab === "eoss" && <EossStep form={form} set={set} eossActive={eossActive} />}
            {subTab === "returns" && <ReturnsStep form={form} set={set} eossActive={eossActive} />}
        </div>
    );
}

function StoreStep({ form, set, storeOptions, locationOptions, outletToCity, lockStore, lockLocation, lockStaff, user }) {
    const onPickStore = (v) => {
        set("store_name", v);
        if (outletToCity && outletToCity[v]) set("location", outletToCity[v]);
    };
    return (
        <div className="space-y-5">
            {user?.role !== "admin" && user?.role !== "spykar_admin" && user?.role !== "system_admin" && (
                <div className="flex flex-wrap items-center gap-2 rounded-md border border-teal-600/30 bg-teal-50/40 px-3 py-2 text-[12px] dark:bg-teal-950/20">
                    <Lock className="h-3.5 w-3.5 text-teal-700" />
                    <span className="text-muted-foreground">Signed in as</span>
                    <span className="font-semibold text-teal-700">{user?.name}</span>
                    <span className="text-muted-foreground">·</span>
                    <span className="rounded-sm bg-teal-600/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-teal-700">
                        {user?.role}
                    </span>
                    <span className="ml-auto text-[11px] text-muted-foreground">Auto-filled from your assigned outlet</span>
                </div>
            )}
            <Field num={1} label="DATE" required>
                <input type="date" data-testid="gdsr-q1-date" value={form.date}
                    onChange={(e) => set("date", e.target.value)}
                    className="w-full rounded-md border border-border bg-background px-3 py-2 text-[14px]" />
            </Field>
            <Field num={2} label="STORE NAME" required locked={lockStore}>
                {lockStore ? <LockedValue value={form.store_name} testid="gdsr-q2-store-locked" /> : (
                    <ChoiceCards testidPrefix="gdsr-q2-store" value={form.store_name} options={storeOptions} onChange={onPickStore} />
                )}
            </Field>
            <Field num={3} label="LOCATION" required locked={lockLocation}>
                {lockLocation ? <LockedValue value={form.location} testid="gdsr-q3-location-locked" /> : (
                    <ChoiceCards testidPrefix="gdsr-q3-location" value={form.location} options={locationOptions} onChange={(v) => set("location", v)} />
                )}
            </Field>
            <Field num={4} label="STAFF NAME" required locked={lockStaff}>
                <input type="text" data-testid="gdsr-q4-staff" placeholder="Enter your answer"
                    value={form.staff_name} onChange={(e) => set("staff_name", e.target.value)}
                    readOnly={lockStaff}
                    className={`w-full rounded-md border border-border px-3 py-2 text-[14px] ${lockStaff ? "bg-muted/60 text-muted-foreground" : "bg-background"}`} />
            </Field>
        </div>
    );
}

function FreshStep({ form, set }) {
    const catTotal = CAT_KEYS.reduce((a, [k]) => a + n(form[`fresh_cat_${k}`]), 0);
    return (
        <div className="space-y-5">
            <SectionHeader title="Fresh · Totals" color="emerald" />
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <Field label="FRESH QTY" required>
                    <NumberInput testid="gdsr-fresh-qty" integer value={form.fresh_qty} onChange={(v) => set("fresh_qty", v)} />
                </Field>
                <Field label="FRESH MRP VAL" required>
                    <NumberInput testid="gdsr-fresh-mrp" value={form.fresh_mrp_val} onChange={(v) => set("fresh_mrp_val", v)} />
                </Field>
                <Field label="FRESH NET VAL" required>
                    <NumberInput testid="gdsr-fresh-net" value={form.fresh_net_val} onChange={(v) => set("fresh_net_val", v)} />
                </Field>
            </div>
            <SectionHeader title="Fresh · Category-wise Qty Sold" color="emerald" />
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                {CAT_KEYS.map(([k, label]) => (
                    <Field key={k} label={label}>
                        <NumberInput testid={`gdsr-fresh-cat-${k}`} integer value={form[`fresh_cat_${k}`]} onChange={(v) => set(`fresh_cat_${k}`, v)} />
                    </Field>
                ))}
            </div>
            <div className="rounded-md border border-emerald-600/30 bg-emerald-50/40 px-4 py-2 text-[12px] dark:bg-emerald-950/20">
                Fresh category qty total: <span className="ml-1 font-semibold text-emerald-700">{catTotal}</span>
            </div>
        </div>
    );
}

function EossStep({ form, set, eossActive }) {
    const catTotal = CAT_KEYS.reduce((a, [k]) => a + n(form[`eoss_cat_${k}`]), 0);
    if (!eossActive) {
        return (
            <div className="rounded-md border border-amber-500/30 bg-amber-50/40 p-6 text-center text-[13px] text-amber-800 dark:bg-amber-950/20" data-testid="gdsr-eoss-disabled">
                <AlertCircle className="mx-auto mb-2 h-6 w-6" />
                EOSS is <strong>not active</strong> for this location. This tab and Return Order EOSS block are disabled until the configured EOSS period begins.
            </div>
        );
    }
    return (
        <div className="space-y-5">
            <SectionHeader title="EOSS · Totals" color="amber" />
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <Field label="EOSS QTY" required>
                    <NumberInput testid="gdsr-eoss-qty" integer value={form.eoss_qty} onChange={(v) => set("eoss_qty", v)} />
                </Field>
                <Field label="EOSS MRP VAL" required>
                    <NumberInput testid="gdsr-eoss-mrp" value={form.eoss_mrp_val} onChange={(v) => set("eoss_mrp_val", v)} />
                </Field>
                <Field label="EOSS NET VAL" required>
                    <NumberInput testid="gdsr-eoss-net" value={form.eoss_net_val} onChange={(v) => set("eoss_net_val", v)} />
                </Field>
            </div>
            <SectionHeader title="EOSS · Category-wise Qty Sold" color="amber" />
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                {CAT_KEYS.map(([k, label]) => (
                    <Field key={k} label={label}>
                        <NumberInput testid={`gdsr-eoss-cat-${k}`} integer value={form[`eoss_cat_${k}`]} onChange={(v) => set(`eoss_cat_${k}`, v)} />
                    </Field>
                ))}
            </div>
            <div className="rounded-md border border-amber-600/30 bg-amber-50/40 px-4 py-2 text-[12px] dark:bg-amber-950/20">
                EOSS category qty total: <span className="ml-1 font-semibold text-amber-700">{catTotal}</span>
            </div>
        </div>
    );
}

function ReturnsStep({ form, set, eossActive }) {
    return (
        <div className="space-y-5">
            <SectionHeader title="Fresh Returns" color="emerald" />
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <Field label="RETURN QTY">
                    <NumberInput testid="gdsr-fresh-ret-qty" integer value={form.fresh_returns_qty} onChange={(v) => set("fresh_returns_qty", v)} />
                </Field>
                <Field label="FRESH MRP VALUE">
                    <NumberInput testid="gdsr-fresh-ret-mrp" value={form.fresh_returns_mrp_val} onChange={(v) => set("fresh_returns_mrp_val", v)} />
                </Field>
                <Field label="FRESH NET VALUE">
                    <NumberInput testid="gdsr-fresh-ret-net" value={form.fresh_returns_net_val} onChange={(v) => set("fresh_returns_net_val", v)} />
                </Field>
            </div>
            <SectionHeader title={`EOSS Returns${eossActive ? "" : " (disabled — EOSS not active)"}`} color="amber" />
            <div className={`grid grid-cols-1 gap-3 sm:grid-cols-3 ${!eossActive ? "opacity-50 pointer-events-none" : ""}`}>
                <Field label="RETURN QTY">
                    <NumberInput testid="gdsr-eoss-ret-qty" integer value={form.eoss_returns_qty} onChange={(v) => set("eoss_returns_qty", v)} />
                </Field>
                <Field label="EOSS MRP VALUE">
                    <NumberInput testid="gdsr-eoss-ret-mrp" value={form.eoss_returns_mrp_val} onChange={(v) => set("eoss_returns_mrp_val", v)} />
                </Field>
                <Field label="EOSS NET VALUE">
                    <NumberInput testid="gdsr-eoss-ret-net" value={form.eoss_returns_net_val} onChange={(v) => set("eoss_returns_net_val", v)} />
                </Field>
            </div>
            <Field label="REMARKS (recommended)">
                <textarea data-testid="gdsr-returns-remarks" value={form.returns_remarks}
                    onChange={(e) => set("returns_remarks", e.target.value)} rows={3}
                    placeholder="Reason for return, batch info, etc."
                    className="w-full rounded-md border border-border bg-background px-3 py-2 text-[14px]" />
            </Field>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Other top-level tabs
// ---------------------------------------------------------------------------
function GiftTab({ form, set }) {
    const groups = [
        [25, "q25_gift_box_package", "What package would you like for the gift box?", "radio", ["Large", "Medium", "Small"]],
        [26, "q26_gift_box_set", "Select gift box set", "radio", ["Gift Hamper", "Classic Hamper"]],
        [27, "q27_quantity", "Quantity", "text"],
        [28, "q28_gift_for", "Who is the gift box being purchased for?", "radio", [
            "A colleague or boss", "A family member (e.g., parent, sibling, child)", "A friend or partner",
            "A client or business associate", "Self-purchase (for personal use)", "A teacher or mentor",
        ]],
        [29, "q29_personalized_message", "Would you like a personalized message added?", "radio", ["Yes", "No"]],
        [30, "q30_personalized_message_to", "Personalized message addressed to", "text"],
        [31, "q31_personalized_message_text", "Personalized message text", "text"],
        [32, "q32_ship_to_receiver", "Ship gift directly to the receiver?", "radio", ["Yes", "No"]],
        [33, "q33_gift_receiver_address", "Address of gift receiver", "text"],
    ];
    return <QuestionGrid groups={groups} form={form} set={set} testidPrefix="gdsr-gift" />;
}

function DeliveryTab({ form, set }) {
    const groups = [
        [18, "q18_full_name", "Full name", "text"],
        [19, "q19_phone_number", "Phone number", "text"],
        [20, "q20_email", "Email", "text"],
        [21, "q21_shipping_address", "Shipping address", "text"],
        [22, "q22_city", "City", "text"],
        [23, "q23_state", "State", "text"],
        [24, "q24_zip_code", "ZIP code", "text"],
        [34, "q34_payment_method", "Payment method", "radio", ["UPI", "Net Banking", "Cards"]],
        [35, "q35_billing_address", "Billing address", "text"],
        [36, "q36_hear_about", "How did you hear about this company?", "radio", ["Yes", "No"]],
        [37, "q37_further_comments", "Further comments / special requirements", "text"],
        [38, "q38_email_address", "Email address", "text"],
        [39, "q39_address", "Address", "text"],
    ];
    return <QuestionGrid groups={groups} form={form} set={set} testidPrefix="gdsr-delivery" />;
}

function PurifierTab({ form, set }) {
    const groups = [
        [40, "q40_appointment_date", "Appointment date", "date"],
        [41, "q41_appointment_time", "Appointment time", "text"],
        [42, "q42_water_purifier_model", "Water purifier model in use", "text"],
        [43, "q43_water_purifier_years", "Years of use", "text"],
        [44, "q44_water_purifier_warranty", "Under warranty?", "radio", ["Yes", "No"]],
        [45, "q45_water_purifier_problem", "Describe the problem", "text"],
        [46, "q46_special_instructions", "Special instructions", "text"],
    ];
    return <QuestionGrid groups={groups} form={form} set={set} testidPrefix="gdsr-purifier" />;
}

function QuestionGrid({ groups, form, set, testidPrefix }) {
    return (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {groups.map((q) => {
                const [num, key, label, type, options] = q;
                return (
                    <Field key={key} num={num} label={label}>
                        {type === "text" && (
                            <input type="text" data-testid={`${testidPrefix}-${key}`}
                                value={form[key]} onChange={(e) => set(key, e.target.value)}
                                placeholder="Enter your answer"
                                className="w-full rounded-md border border-border bg-background px-3 py-2 text-[14px]" />
                        )}
                        {type === "date" && (
                            <input type="date" data-testid={`${testidPrefix}-${key}`}
                                value={form[key]} onChange={(e) => set(key, e.target.value)}
                                className="w-full rounded-md border border-border bg-background px-3 py-2 text-[14px]" />
                        )}
                        {type === "radio" && (
                            <ChoiceCards testidPrefix={`${testidPrefix}-${key}`}
                                value={form[key]} options={options} onChange={(v) => set(key, v)} />
                        )}
                    </Field>
                );
            })}
        </div>
    );
}

// ---------------------------------------------------------------------------
// Review + Submit
// ---------------------------------------------------------------------------
function ReviewTab({ form, onEdit, onSubmit, saving, eossActive }) {
    const sections = [
        { title: "Store Details", tab: "sales", items: [
            ["Date", form.date], ["Store", form.store_name], ["Location", form.location], ["Staff", form.staff_name],
        ]},
        { title: "Fresh", tab: "sales", items: [
            ["Fresh Qty", form.fresh_qty], ["Fresh MRP", form.fresh_mrp_val], ["Fresh Net", form.fresh_net_val],
        ]},
        { title: "EOSS", tab: "sales", items: eossActive ? [
            ["EOSS Qty", form.eoss_qty], ["EOSS MRP", form.eoss_mrp_val], ["EOSS Net", form.eoss_net_val],
        ] : [["EOSS Status", "Not active for this location"]]},
        { title: "Returns", tab: "sales", items: [
            ["Fresh Ret Qty", form.fresh_returns_qty || "—"],
            ["EOSS Ret Qty", form.eoss_returns_qty || "—"],
        ]},
    ];
    return (
        <div className="space-y-4">
            <div className="text-[12px] text-muted-foreground">
                Please review your answers before submitting. Click any section to jump back and edit.
            </div>
            {sections.map((s) => (
                <div key={s.title} className="rounded-md border border-border">
                    <div className="flex items-center justify-between border-b border-border bg-muted/30 px-3 py-1.5">
                        <div className="text-[12px] font-semibold">{s.title}</div>
                        <button onClick={() => onEdit(s.tab)}
                            className="text-[11px] font-semibold text-teal-600 hover:underline"
                            data-testid={`gdsr-review-edit-${s.title.toLowerCase().replace(/\s+/g, "-")}`}>
                            Edit
                        </button>
                    </div>
                    <div className="grid grid-cols-2 gap-1 p-3 sm:grid-cols-3">
                        {s.items.map(([label, val]) => (
                            <div key={label} className="flex items-baseline justify-between gap-2 border-b border-border/40 py-1 text-[12px]">
                                <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</span>
                                <span className="font-medium">{val === "" || val === undefined ? "—" : String(val)}</span>
                            </div>
                        ))}
                    </div>
                </div>
            ))}
            <div className="flex justify-end pt-2">
                <button onClick={onSubmit} disabled={saving} data-testid="gdsr-submit"
                    className="inline-flex items-center gap-1 rounded-md bg-teal-600 px-5 py-2 text-[13px] font-semibold text-white hover:bg-teal-700 disabled:opacity-50">
                    {saving ? "Submitting…" : <>Submit Response <Send className="ml-1 h-4 w-4" /></>}
                </button>
            </div>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Responses table + Edit modal (45-day rule)
// ---------------------------------------------------------------------------
function ResponsesTable({ rows, user, onEdit }) {
    const [expanded, setExpanded] = useState(null);
    const isAdmin = user && ADMIN_ROLES.has(user.role);
    const isEditable = (r) => {
        if (isAdmin) return true;
        if (r.created_by !== user?.id) return false;
        const days = (Date.now() - new Date(r.created_at).getTime()) / 86400000;
        return days <= 45;
    };
    return (
        <div className="overflow-hidden rounded-md border border-border bg-card" data-testid="gdsr-responses-table">
            <div className="border-b border-border bg-muted/40 px-4 py-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                Recent Responses · {rows.length}
            </div>
            <div className="max-h-[420px] overflow-auto">
                <table className="w-full text-[12px]">
                    <thead className="sticky top-0 z-10 bg-card text-[10px] uppercase tracking-wider text-muted-foreground shadow-sm">
                        <tr>
                            <th className="w-8 px-2 py-2"></th>
                            <th className="px-3 py-2 text-left">Date</th>
                            <th className="px-3 py-2 text-left">Store</th>
                            <th className="px-3 py-2 text-left">Location</th>
                            <th className="px-3 py-2 text-left">Staff</th>
                            <th className="px-3 py-2 text-right">Fresh Qty</th>
                            <th className="px-3 py-2 text-right">EOSS Qty</th>
                            <th className="px-3 py-2 text-left">By</th>
                            <th className="px-3 py-2 text-center">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r) => (
                            <Fragment key={r.id}>
                                <tr className="border-t border-border hover:bg-muted/40" data-testid={`gdsr-row-${r.id}`}>
                                    <td className="px-2 py-1.5 text-center text-muted-foreground cursor-pointer"
                                        onClick={() => setExpanded(expanded === r.id ? null : r.id)}>
                                        {expanded === r.id ? "▾" : "▸"}
                                    </td>
                                    <td className="px-3 py-1.5">{r.date}</td>
                                    <td className="px-3 py-1.5">{r.store_name}</td>
                                    <td className="px-3 py-1.5">{r.location}</td>
                                    <td className="px-3 py-1.5">{r.staff_name}</td>
                                    <td className="mono-num px-3 py-1.5 text-right">{r.fresh_qty}</td>
                                    <td className="mono-num px-3 py-1.5 text-right">{r.eoss_qty}</td>
                                    <td className="px-3 py-1.5 text-[11px] text-muted-foreground">{r.created_by_name || "—"}</td>
                                    <td className="px-3 py-1.5 text-center">
                                        {isEditable(r) ? (
                                            <button onClick={() => onEdit(r)} data-testid={`gdsr-edit-${r.id}`}
                                                className="inline-flex items-center gap-1 rounded-sm border border-teal-600/40 bg-teal-50/60 px-2 py-0.5 text-[11px] font-medium text-teal-700 hover:bg-teal-100">
                                                <Pencil className="h-3 w-3" /> Edit
                                            </button>
                                        ) : (
                                            <span className="text-[10px] text-muted-foreground">Locked</span>
                                        )}
                                    </td>
                                </tr>
                                {expanded === r.id && (
                                    <tr className="border-t border-border bg-muted/20">
                                        <td colSpan={9} className="px-4 py-3">
                                            <ExpandedDetail row={r} />
                                        </td>
                                    </tr>
                                )}
                            </Fragment>
                        ))}
                        {rows.length === 0 && (
                            <tr><td colSpan={9} className="px-3 py-8 text-center text-muted-foreground">No responses yet.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function ExpandedDetail({ row }) {
    const items = [
        ["Fresh Qty", row.fresh_qty], ["Fresh MRP", row.fresh_mrp_val], ["Fresh Net", row.fresh_net_val],
        ["EOSS Qty", row.eoss_qty], ["EOSS MRP", row.eoss_mrp_val], ["EOSS Net", row.eoss_net_val],
        ["Fresh Ret Qty", row.fresh_returns_qty || 0], ["EOSS Ret Qty", row.eoss_returns_qty || 0],
    ];
    return (
        <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
            {items.map(([label, val]) => (
                <div key={label} className="rounded-sm border border-border/60 bg-background px-2 py-1 text-[11px]">
                    <div className="uppercase tracking-wider text-muted-foreground">{label}</div>
                    <div className="font-semibold">{val ?? "—"}</div>
                </div>
            ))}
        </div>
    );
}

function EditModal({ row, onClose, onSaved }) {
    const [remarks, setRemarks] = useState("");
    const [saving, setSaving] = useState(false);
    const [values, setValues] = useState({
        fresh_qty: row.fresh_qty, fresh_mrp_val: row.fresh_mrp_val, fresh_net_val: row.fresh_net_val,
        eoss_qty: row.eoss_qty, eoss_mrp_val: row.eoss_mrp_val, eoss_net_val: row.eoss_net_val,
    });
    const submit = async () => {
        if (!remarks.trim()) { toast.error("Remarks are required for every edit"); return; }
        setSaving(true);
        try {
            const updates = {};
            Object.keys(values).forEach((k) => {
                if (Number(values[k]) !== Number(row[k])) updates[k] = Number(values[k]);
            });
            if (Object.keys(updates).length === 0) { toast.error("No changes detected"); setSaving(false); return; }
            const r = await http.put(`/reports/gujrat-dsr/${row.id}`, { updates, remarks: remarks.trim() });
            toast.success("Updated");
            onSaved(r.data);
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        } finally { setSaving(false); }
    };
    return (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4" data-testid="gdsr-edit-modal">
            <div className="w-full max-w-lg rounded-md border border-border bg-card shadow-xl">
                <div className="flex items-center justify-between border-b border-border p-4">
                    <div>
                        <div className="text-[14px] font-semibold">Edit Response</div>
                        <div className="text-[11px] text-muted-foreground">{row.store_name} · {row.date}</div>
                    </div>
                    <button onClick={onClose} className="rounded-sm p-1 hover:bg-muted"><X className="h-4 w-4" /></button>
                </div>
                <div className="space-y-3 p-4">
                    <div className="grid grid-cols-3 gap-2">
                        {Object.keys(values).map((k) => (
                            <Field key={k} label={k.replace(/_/g, " ").toUpperCase()}>
                                <NumberInput testid={`gdsr-edit-${k}`} integer={k.endsWith("_qty")} value={values[k]}
                                    onChange={(v) => setValues((s) => ({ ...s, [k]: v }))} />
                            </Field>
                        ))}
                    </div>
                    <Field label="REMARKS (mandatory)">
                        <textarea data-testid="gdsr-edit-remarks" value={remarks}
                            onChange={(e) => setRemarks(e.target.value)} rows={3}
                            placeholder="Why are you editing this row?"
                            className="w-full rounded-md border border-border bg-background px-3 py-2 text-[13px]" />
                    </Field>
                </div>
                <div className="flex justify-end gap-2 border-t border-border p-3">
                    <button onClick={onClose} className="rounded-md border border-border px-3 py-1.5 text-[12px]">Cancel</button>
                    <button onClick={submit} disabled={saving} data-testid="gdsr-edit-save"
                        className="rounded-md bg-teal-600 px-4 py-1.5 text-[12px] font-semibold text-white disabled:opacity-50">
                        {saving ? "Saving…" : "Save with Remarks"}
                    </button>
                </div>
            </div>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Primitives
// ---------------------------------------------------------------------------
function Field({ num, label, required, locked, children }) {
    return (
        <div className="space-y-1.5">
            <div className="flex items-baseline gap-2 text-[13px] font-medium">
                {num && <span className="rounded-sm bg-muted px-1.5 py-0.5 text-[10px] font-bold text-muted-foreground">Q{num}</span>}
                <span>{label}</span>
                {required && <span className="ml-0.5 text-red-500">*</span>}
                {locked && (
                    <span className="ml-1 rounded-sm bg-teal-600/10 px-1 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-teal-700">Locked</span>
                )}
            </div>
            {children}
        </div>
    );
}

function SectionHeader({ title, color }) {
    const cls = {
        emerald: "text-emerald-700 border-emerald-500/30",
        amber: "text-amber-700 border-amber-500/30",
    }[color] || "text-muted-foreground border-border";
    return (
        <div className={`border-b pb-2 text-[11px] font-semibold uppercase tracking-wider ${cls}`}>
            {title}
        </div>
    );
}

function LockedValue({ value, testid }) {
    return (
        <div data-testid={testid} className="flex items-center gap-2 rounded-md border border-border bg-muted/60 px-3 py-2 text-[14px]">
            <Lock className="h-3.5 w-3.5 shrink-0 text-teal-600" />
            <span className="font-medium text-foreground">{value || "—"}</span>
            <span className="ml-auto text-[10px] uppercase tracking-wider text-teal-700">Auto-filled</span>
        </div>
    );
}

function ChoiceCards({ value, options, onChange, testidPrefix }) {
    return (
        <div className="flex flex-wrap gap-2">
            {options.map((o, i) => {
                const selected = value === o;
                return (
                    <button key={o} type="button" onClick={() => onChange(o)}
                        data-testid={`${testidPrefix}-opt-${i}`}
                        className={[
                            "rounded-md border px-3 py-1.5 text-[12.5px] transition",
                            selected
                                ? "border-teal-600 bg-teal-600 font-semibold text-white"
                                : "border-border bg-background hover:border-teal-600/40 hover:bg-muted/30",
                        ].join(" ")}>
                        {o}
                    </button>
                );
            })}
        </div>
    );
}

function NumberInput({ testid, value, onChange, integer }) {
    return (
        <input type="number" step={integer ? "1" : "any"} min="0" placeholder="0"
            data-testid={testid} value={value ?? ""}
            onKeyDown={(e) => {
                if (integer && (e.key === "." || e.key === "," || e.key === "e" || e.key === "E")) {
                    e.preventDefault();
                }
            }}
            onChange={(e) => {
                let v = e.target.value;
                if (integer && v !== "") {
                    v = String(Math.trunc(Math.abs(Number(v))));
                }
                onChange(v);
            }}
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-[14px]" />
    );
}
